import * as React from 'react';
import { PaginationNavigation, SpinnerWrapper, SortTableContext } from "soprasteria-ui-library";
import { useAuth } from '../../../App';
import { ReferralSearchApi } from '../../../api/ReferralSearchApi';
import { IReferralSearchParams, IReferral } from '../../../models/IReferral';
import { ReferralSearchForm } from "./ReferralSearchForm";
import { ReferralSearchTable } from "./ReferralSearchTable";
import { IPagination } from 'soprasteria-ui-library/dist/models/IPagination';
import { useHistory } from 'react-router';
import { isInRole, UserContext } from '../../../contexts/UserContext';
import { SPECIALISTTANDVÅRDBAS } from '../../../utils/RoleConstants';
import { SortOrder } from '../../../models/IPage';
import { REFERRAL_SEARCH } from '../../../utils/RouteConstants';

export const ReferralSearch: React.FunctionComponent<{}> = () => {
    const auth = useAuth();
    const api = new ReferralSearchApi(auth);
    const abortController = new AbortController();
    const [paginationHeader, setPaginationHeader] = React.useState<IPagination>();
    const [referrals, setReferrals] = React.useState<IReferral[]>();
    const [isFetchingReferrals, setIsFetchingReferrals] = React.useState<boolean>(false);
    const [searchParams, setSearchParams] = React.useState<IReferralSearchParams>();
    const pageSize = 20;
    const { sortOrder, selectedPropertyToSortBy, setSelectedPropertyToSortBy, setSortOrder } = React.useContext(SortTableContext);
    const history = useHistory();
    const userInfo = React.useContext(UserContext);
    const hasRoleSpecialisttandvard = isInRole(SPECIALISTTANDVÅRDBAS, userInfo.roles ? userInfo.roles : []);
    const defaultOrderBy = "ReferralDate";

    React.useEffect(() => {
        document.getElementById("clear-form")?.addEventListener("click", resetValue);

        if (setSelectedPropertyToSortBy && setSortOrder) {
            if (history.location.state !== undefined && history.location.state !== null) {
                const lastSearchParams = history.location.state as IReferralSearchParams;
                setSelectedPropertyToSortBy(lastSearchParams.propertyToOrderBy ?? defaultOrderBy);
                setSortOrder(lastSearchParams.sortOrder ?? SortOrder.descending);
                searchReferrals(lastSearchParams);
            }
            else {
                setSelectedPropertyToSortBy(defaultOrderBy);
                setSortOrder(SortOrder.descending);
                searchReferrals({ pageSize: pageSize, pageNumber: 1, propertyToOrderBy: defaultOrderBy, sortOrder: SortOrder.descending });
            }
        }

        return () => {
            abortController.abort();
            document.getElementById("clear-form")?.removeEventListener("click", resetValue);
        }
    }, [])

    React.useEffect(() => {
        if (referrals) {
            onSortOrderClick();
        }
    }, [selectedPropertyToSortBy, sortOrder])

    const resetValue = () => {
        history.push(REFERRAL_SEARCH, undefined);
    }

    const searchReferrals = (data: IReferralSearchParams) => {
        setIsFetchingReferrals(true);
        data.pageSize = pageSize;
        data.pageNumber = data.pageNumber ?? 1;
        data.propertyToOrderBy = data.propertyToOrderBy ?? selectedPropertyToSortBy;
        data.sortOrder = data.sortOrder ?? sortOrder;

        if (hasRoleSpecialisttandvard && data.includeArchivedReferrals === undefined) {
            data.includeArchivedReferrals = false;
        }

        history.push(REFERRAL_SEARCH, data);

        api.searchReferrals(data, false, (response) => {
            setReferrals(response.data);
            setPaginationHeader(response.paginationHeader);
        }, "Specialistremisser", undefined, () => setIsFetchingReferrals(false));

        setSearchParams(data);
    }

    const onPageSelect = (pageNumber: number) => {
        if (searchParams) {
            setIsFetchingReferrals(true);
            var currentSearchParams = searchParams;
            currentSearchParams.pageNumber = pageNumber;
            history.push(REFERRAL_SEARCH, currentSearchParams);

            api.searchReferrals(currentSearchParams, false, (response) => {
                setReferrals(response.data);
                setPaginationHeader(response.paginationHeader);
            }, "", undefined, () => setIsFetchingReferrals(false));

        }
    }

    const onSortOrderClick = () => {
        if (searchParams) {
            setIsFetchingReferrals(true);
            var currentSearchParams = searchParams;
            currentSearchParams.pageNumber = 1;
            currentSearchParams.sortOrder = sortOrder;
            currentSearchParams.propertyToOrderBy = selectedPropertyToSortBy;
            history.push(REFERRAL_SEARCH, currentSearchParams);

            api.searchReferrals(currentSearchParams, false, (response) => {
                setReferrals(response.data);
                setPaginationHeader(response.paginationHeader);
            }, "", undefined, () => setIsFetchingReferrals(false));
        }
    }

    return (
        <SpinnerWrapper isLoading={isFetchingReferrals}>
            <section className="card">
                <div className="card-header">
                    <h1>Sök specialistremisser</h1>
                </div>
                <div className="card-body">
                    <ReferralSearchForm searchReferrals={searchReferrals} preSetSearchParams={history.location.state !== undefined && history.location.state !== null ? history.location.state as any : undefined} />
                    {
                        referrals ?
                            <>
                                <ReferralSearchTable referrals={referrals} />
                                <PaginationNavigation
                                    ariaLabel="received referrals pagination selector"
                                    pagination={paginationHeader}
                                    pageNeighbours={2}
                                    selectedPage={page => onPageSelect(page)}
                                    showCount={true} />
                            </>
                            : null
                    }
                </div>
            </section>
        </SpinnerWrapper>
    )
}